import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { FolderKanban, FileText, Receipt, Shield, Wrench } from 'lucide-react';

/**
 * TEMA A3 (video de Alian "Relaciones entre estimado e invoice"): barra de
 * documentos relacionados (Proyecto · Estimado(s) · Invoice(s) · Trabajo(s) ·
 * Permiso(s)) con link directo a cada uno, para "poder navegar dentro de
 * ello" sin volver a la lista. Usada en el detalle de Job, Estimate, Invoice
 * y Permit — mismo componente, mismo endpoint GET /api/relations/{type}/{id}.
 */
const KIND_META = {
  estimate: { icon: FileText, label: 'Estimate', color: 'text-violet-600 bg-violet-50 border-violet-200' },
  invoice: { icon: Receipt, label: 'Invoice', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  job: { icon: Wrench, label: 'Job', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  permit: { icon: Shield, label: 'Permit', color: 'text-amber-600 bg-amber-50 border-amber-200' },
};

export default function RelatedDocumentsBar({ entityType, entityId, className = '' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!entityType || !entityId) { setData(null); return; }
    api.get(`/relations/${entityType}/${entityId}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [entityType, entityId]);

  const goTo = (kind, id) => {
    if (kind === 'estimate') navigate('/estimates', { state: { openEstimateId: id } });
    else if (kind === 'invoice') navigate('/invoices', { state: { openInvoiceId: id } });
    else if (kind === 'job') navigate(`/jobs/${id}`);
    else if (kind === 'permit') navigate(`/permits?open=${id}`);
    else if (kind === 'project') navigate(`/projects/${id}`);
  };

  if (!data) return null;
  const chips = [];
  if (data.project) chips.push({ kind: 'project', id: data.project.id, number: data.project.number || data.project.title });
  for (const e of data.estimates || []) chips.push({ kind: 'estimate', id: e.id, number: e.number });
  for (const i of data.invoices || []) chips.push({ kind: 'invoice', id: i.id, number: i.number });
  for (const j of data.jobs || []) chips.push({ kind: 'job', id: j.id, number: j.number });
  for (const p of data.permits || []) chips.push({ kind: 'permit', id: p.id, number: p.number });
  if (chips.length === 0) return null;

  return (
    <div data-testid="related-documents-bar" className={`flex items-center gap-1.5 flex-wrap ${className}`}>
      {chips.map((c, idx) => {
        if (c.kind === 'project') {
          return (
            <button
              key={`project-${c.id}`}
              type="button"
              data-testid="related-doc-project"
              onClick={() => goTo('project', c.id)}
              className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100 transition-colors"
            >
              <FolderKanban className="w-3 h-3" /> {c.number || 'Project'}
            </button>
          );
        }
        const meta = KIND_META[c.kind];
        const Icon = meta.icon;
        return (
          <button
            key={`${c.kind}-${c.id}-${idx}`}
            type="button"
            data-testid={`related-doc-${c.kind}-${c.id}`}
            onClick={() => goTo(c.kind, c.id)}
            className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border hover:opacity-80 transition-opacity ${meta.color}`}
          >
            <Icon className="w-3 h-3" /> {c.number || meta.label}
          </button>
        );
      })}
    </div>
  );
}
